import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, MessageSquare, LogOut, ShieldAlert, Menu, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/browse", label: "Browse cars", id: "nav-browse" },
  { to: "/dashboard", label: "My garage", id: "nav-garage" },
  { to: "/messages", label: "Messages", id: "nav-messages" },
];
const PRICING = [
  { to: "/pricing/dealers", label: "Dealerships", id: "nav-pricing-dealers" },
  { to: "/pricing/private", label: "Private sellers", id: "nav-pricing-private" },
];

function PricingMenu({ cls }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button data-testid="nav-pricing" className={`inline-flex items-center gap-1 outline-none ${cls}`}>Pricing <ChevronDown className="w-3.5 h-3.5" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-48 bg-white">
        {PRICING.map((p) => <DropdownMenuItem asChild key={p.to} data-testid={p.id}><Link to={p.to}>{p.label}</Link></DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ user, onLogout }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="nav-user-menu" className="rounded-full">
          <Avatar className="w-9 h-9 border border-white/40">
            <AvatarImage src={user.picture} />
            <AvatarFallback className="bg-navy text-white text-xs">{(user.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white">
        <div className="px-2 py-1.5 text-sm">
          <p className="font-medium truncate">{user.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="menu-dashboard"><Link to="/dashboard"><LayoutDashboard className="w-4 h-4 mr-2" />My garage</Link></DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="menu-messages"><Link to="/messages"><MessageSquare className="w-4 h-4 mr-2" />Messages</Link></DropdownMenuItem>
        {user.role === "admin" && (
          <DropdownMenuItem asChild data-testid="menu-admin"><Link to="/admin"><ShieldAlert className="w-4 h-4 mr-2" />Admin</Link></DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({ user, light }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className={`md:hidden p-2 ${light ? "text-white" : "text-navy"}`} data-testid="nav-mobile-toggle" aria-label="Open menu"><Menu className="w-6 h-6" /></button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-background w-72">
        <SheetTitle className="font-heading text-2xl font-extrabold tracking-tighter">vorra<span className="text-teal">.</span></SheetTitle>
        <nav className="mt-8 flex flex-col gap-1">
          {[...LINKS.slice(0, 1), ...PRICING, ...LINKS.slice(1)].map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`m-${l.id}`} className="py-3 text-lg font-heading font-semibold border-b border-border">{PRICING.includes(l) ? `Pricing: ${l.label}` : l.label}</Link>
          ))}
          {!user && <Link to="/login" onClick={() => setOpen(false)} data-testid="m-nav-login" className="py-3 text-lg font-heading font-semibold border-b border-border">Sign in</Link>}
          <Button asChild className="mt-6 h-12 rounded-full bg-teal hover:bg-teal-dark text-white" data-testid="m-nav-sell"><Link to="/sell" onClick={() => setOpen(false)}>Sell my car</Link></Button>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = pathname === "/" && !scrolled;
  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <header data-testid="navbar" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 border-b ${scrolled ? "bg-[#EEF3F6]/75 backdrop-blur-xl border-navy/10 shadow-[0_8px_30px_-12px_rgba(22,36,47,0.18)]" : "bg-transparent border-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <Logo light={light} />
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
          {LINKS.map((l, i) => (
            <span key={l.to} className="contents">
              <NavLink to={l.to} data-testid={l.id}
                className={({ isActive }) => `relative transition-colors ${light ? "text-white/85 hover:text-white" : isActive ? "text-teal" : "text-navy/80 hover:text-teal"}`}>
                {l.label}
              </NavLink>
              {i === 0 && <PricingMenu cls={`transition-colors ${light ? "text-white/85 hover:text-white" : pathname.startsWith("/pricing") ? "text-teal" : "text-navy/80 hover:text-teal"}`} />}
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? <UserMenu user={user} onLogout={doLogout} /> : (
            <Link to="/login" data-testid="nav-login-btn" className={`hidden md:inline text-sm font-medium transition-colors ${light ? "text-white hover:text-white/80" : "text-navy hover:text-teal"}`}>Sign in</Link>
          )}
          <Button asChild className="hidden sm:inline-flex rounded-full bg-teal hover:bg-teal-dark text-white px-5 h-10 transition-transform hover:-translate-y-0.5" data-testid="nav-sell-btn">
            <Link to="/sell">Sell my car</Link>
          </Button>
          <MobileMenu user={user} light={light} />
        </div>
      </div>
    </header>
  );
}
